import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Enhanced Page Loader with progress indication
 * Shows during lazy-loaded route transitions
 */
const PageLoader = ({ message = 'Loading page...' }) => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col items-center gap-4 p-8 bg-card rounded-xl shadow-lg">
        <Loader2 className="h-12 w-12 text-brand-500 dark:text-brand-400 animate-spin" />
        <p className="text-sm font-medium text-muted-foreground">{message}</p>
        <div className="w-48 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-brand-500 dark:bg-brand-500 rounded-full animate-pulse" style={{ width: '60%' }}></div>
        </div>
      </div>
    </div>
  );
};

/**
 * Minimal loader for faster perceived performance
 * Use for components that load quickly
 */
export const MinimalLoader = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="h-8 w-8 text-brand-500 animate-spin" />
  </div>
);

/**
 * Inline loader for component-level loading states
 */
export const InlineLoader = ({ text = 'Loading...' }) => (
  <div className="flex items-center justify-center gap-2 p-4">
    <Loader2 className="h-5 w-5 text-brand-500 animate-spin" />
    <span className="text-sm text-gray-600">{text}</span>
  </div>
);

/**
 * Skeleton loader for content that's loading
 */
export const SkeletonLoader = ({ lines = 3 }) => (
  <div className="animate-pulse space-y-4 p-4">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-4 bg-gray-200 rounded" style={{ width: `${100 - i * 10}%` }}></div>
    ))}
  </div>
);

/**
 * Preload a lazy-loaded component
 * Call this to preload routes before user navigates
 */
// eslint-disable-next-line react-refresh/only-export-components
export const preloadRoute = (importFn) => {
  // Trigger the lazy load without rendering
  importFn();
};

/**
 * Hook to preload common routes after initial app load
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePreloadCommonRoutes = () => {
  useEffect(() => {
    // Preload dashboard and products after 2 seconds
    const timer = setTimeout(() => {
      import('../pages/Dashboard').catch(() => {});
      import('../pages/Inventory/Products').catch(() => {});
    }, 2000);

    return () => clearTimeout(timer);
  }, []);
};

export default PageLoader;
